import Phaser from 'phaser';
import { TEX } from '../core/TextureFactory';

export interface HeroConfig {
  x: number;
  y: number;
  hp: number;
  maxHp: number;
  attackIntervalMs: number;
}

const HP_BAR_WIDTH = 28;
/** Duración de cada cuadro del bob de reposo (arquero alternando 2 cuadros). */
const IDLE_FRAME_MS = 420;
/** Cuánto se mantiene visible el cuadro de disparo antes de volver al reposo. */
const ATTACK_FRAME_MS = 160;

/**
 * Héroe jugable: sprite arquero animado (2 cuadros de reposo con bob sutil +
 * 1 cuadro de disparo) en la franja de combate, con barra de HP verde y un
 * icono de escudo que se enciende un instante al bloquear un golpe. El
 * disparo real (a qué enemigo, con qué daño) lo decide `WaveEngine` — esta
 * clase solo lleva el cooldown de ataque, la animación y el estado visual/de
 * vida.
 */
export class Hero {
  readonly view: Phaser.GameObjects.Container;
  private readonly sprite: Phaser.GameObjects.Image;
  private readonly hpBarFill: Phaser.GameObjects.Rectangle;
  private readonly shieldIcon: Phaser.GameObjects.Text;

  hp: number;
  maxHp: number;
  attackIntervalMs: number;
  private attackCooldownMs = 0;

  private idleFrameMs = 0;
  private idleToggle = false;
  private attackFrameMs = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    parent: Phaser.GameObjects.Container,
    config: HeroConfig
  ) {
    this.hp = config.hp;
    this.maxHp = config.maxHp;
    this.attackIntervalMs = config.attackIntervalMs;

    this.view = scene.add.container(config.x, config.y);
    this.sprite = scene.add.image(0, 0, TEX.hero).setOrigin(0.5, 1);
    const hpBarBg = scene.add.rectangle(0, -26, HP_BAR_WIDTH, 5, 0x1b1030).setOrigin(0.5);
    this.hpBarFill = scene.add
      .rectangle(-HP_BAR_WIDTH / 2, -26, HP_BAR_WIDTH, 3, 0x5fd35f)
      .setOrigin(0, 0.5);
    this.shieldIcon = scene.add
      .text(12, -26, '🛡', { fontSize: '12px' })
      .setOrigin(0.5)
      .setAlpha(0);

    this.view.add([hpBarBg, this.hpBarFill, this.sprite, this.shieldIcon]);
    parent.add(this.view);
    this.refreshHpBar();
  }

  get x(): number {
    return this.view.x;
  }

  get y(): number {
    return this.view.y;
  }

  get isDead(): boolean {
    return this.hp <= 0;
  }

  setPosition(x: number, y: number): void {
    this.view.setPosition(x, y);
  }

  /** Aplica daño ya resuelto (fallo/bloqueo/armadura se calculan antes de llamar). */
  takeDamage(amount: number): void {
    this.hp = Math.max(0, this.hp - amount);
    this.refreshHpBar();
  }

  /** Destello blanco breve al recibir un golpe real (no bloqueado/fallado), mismo lenguaje visual que `Enemy.playHitFlash`. */
  playHitFlash(): void {
    this.sprite.setTintFill(0xffffff);
    this.scene.time.delayedCall(80, () => this.sprite.clearTint());
  }

  heal(amount: number): void {
    this.hp = Math.min(this.maxHp, this.hp + amount);
    this.refreshHpBar();
  }

  /** Reasigna vida (usado por `startWave` con el HP persistido y por revive). */
  setMaxHp(maxHp: number, hp: number): void {
    this.maxHp = maxHp;
    this.hp = Phaser.Math.Clamp(hp, 0, maxHp);
    this.refreshHpBar();
  }

  /** Parpadeo breve del icono de escudo, para marcar un golpe bloqueado. */
  flashShield(): void {
    this.shieldIcon.setAlpha(1);
    this.scene.tweens.add({ targets: this.shieldIcon, alpha: 0, duration: 450, delay: 150 });
  }

  /** Avanza el cooldown de ataque; devuelve true cuando toca disparar (y lo reinicia). */
  tickAttackCooldown(deltaMs: number): boolean {
    this.attackCooldownMs -= deltaMs;
    if (this.attackCooldownMs <= 0) {
      this.attackCooldownMs += this.attackIntervalMs;
      return true;
    }
    return false;
  }

  /**
   * Avanza la animación del arquero: mientras no esté disparando, alterna
   * entre los 2 cuadros de reposo cada `IDLE_FRAME_MS` (bob sutil); llamar
   * cada frame junto con `tickAttackCooldown`.
   */
  updateAnim(deltaMs: number): void {
    if (this.attackFrameMs > 0) {
      this.attackFrameMs -= deltaMs;
      if (this.attackFrameMs <= 0) {
        this.attackFrameMs = 0;
        this.applyIdleFrame();
      }
      return; // el cuadro de disparo tiene prioridad sobre el bob de reposo
    }
    this.idleFrameMs += deltaMs;
    if (this.idleFrameMs >= IDLE_FRAME_MS) {
      this.idleFrameMs -= IDLE_FRAME_MS;
      this.idleToggle = !this.idleToggle;
      this.applyIdleFrame();
    }
  }

  /** Cambia brevemente al cuadro de disparo (arco tensado); llamar cuando el héroe suelta una flecha. */
  playAttackAnim(): void {
    this.attackFrameMs = ATTACK_FRAME_MS;
    this.sprite.setTexture(TEX.heroAttack);
  }

  private applyIdleFrame(): void {
    this.sprite.setTexture(this.idleToggle ? TEX.heroIdle2 : TEX.hero);
  }

  private refreshHpBar(): void {
    const pct = this.maxHp > 0 ? Phaser.Math.Clamp(this.hp / this.maxHp, 0, 1) : 0;
    this.hpBarFill.width = HP_BAR_WIDTH * pct;
  }

  destroy(): void {
    this.view.destroy();
  }
}
